import { isSome, Maybe } from '@crocodille/util';
import { OrderPaymentState } from '@crocodille/common';
import type { GetServerSidePropsContext } from 'next';

export type WebviewMessage =
  | {
      action: 'signOut';
    }
  | {
      action: 'routeChangeComplete';
      route: string;
    }
  | {
      action: 'couponApplied';
      tokenValue: string;
    }
  | {
      action: 'copyToClipboard';
      text: string;
    }
  | {
      action: 'paymentStateChange';
      tokenValue: Maybe<string>;
      paymentState: OrderPaymentState;
      paymentError: Maybe<string>;
    }
  | {
      action: 'repeatOrder';
      tokenValue: string;
    };

export function isAndroidWebView(
  req?: GetServerSidePropsContext['req'],
): boolean {
  return isSome(req) && req.headers['x-requested-with'] === 'cz.bbdomu.app';
}

export function isIOSWebView(req?: GetServerSidePropsContext['req']): boolean {
  if (isSome(req)) {
    const userAgent = req.headers['user-agent']?.toLowerCase?.();

    if (userAgent) {
      const safari = /safari/.test(userAgent);
      const ios = /iphone|ipod|ipad/.test(userAgent);

      return ios && !safari;
    }
  }

  return false;
}

export function isInWebView(req?: GetServerSidePropsContext['req']): boolean {
  if (isSome(req)) {
    if (req.headers['x-webview'] === 'true') {
      return true; // Custom header
    }

    if (isAndroidWebView(req)) {
      return true; // Android WebView
    }

    return isIOSWebView(req); // iOS WebView
  }

  return typeof window !== 'undefined' && isSome(window.ReactNativeWebView);
}

export function postMessageToWebView(message: WebviewMessage): void {
  if (typeof window === 'undefined') {
    return;
  }
  const msg = JSON.stringify(message);

  // We need no send message to iOS differently if ApplePay is enabled:
  // https://github.com/react-native-webview/react-native-webview/issues/920#issuecomment-953801850
  window.webkit?.messageHandlers?.ReactNativeWebView?.postMessage?.(msg); // iOS
  window.ReactNativeWebView?.postMessage?.(msg); // Android
}
